import React, { useState, useEffect } from "react";
import "./../../../../assets/css/general/blog/BlogDetailPage/BlogDetailPage.css";

const BlogDetailPage = () => {
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setReadingProgress(progress);

      const sections = [
        "introduction",
        "core-concepts",
        "example-code",
        "best-practices",
        "conclusion",
      ];
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const relatedArticles = [
    {
      id: 1,
      title: "Advanced React Patterns You Should Know",
      image:
        "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=250&fit=crop",
      readTime: "10 min",
    },
    {
      id: 2,
      title: "State Management in Modern React Apps",
      image:
        "https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=400&h=250&fit=crop",
      readTime: "12 min",
    },
    {
      id: 3,
      title: "Performance Optimization Tips for React",
      image:
        "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=250&fit=crop",
      readTime: "8 min",
    },
  ];

  return (
    <div className="blog-detail-page">
      <div
        className="reading-progress-bar"
        style={{ width: `${readingProgress}%` }}
      ></div>

      <section className="blog-detail-hero">
        <div className="blog-detail-hero-image-wrapper">
          <img
            src="https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=1400&h=600&fit=crop"
            alt="Complete React Developer Roadmap 2026"
            className="blog-detail-hero-image"
          />
          <span className="blog-detail-hero-badge">Frontend Roadmap</span>
        </div>

        <div className="blog-detail-hero-content">
          <h1 className="blog-detail-hero-title">
            Complete React Developer Roadmap 2026
          </h1>

          <div className="blog-detail-hero-meta">
            <div className="blog-detail-author-info">
              <img
                src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
                alt="Sarah Chen"
                className="blog-detail-author-avatar"
              />
              <div className="blog-detail-author-text">
                <span className="blog-detail-author-name">Sarah Chen</span>
                <div className="blog-detail-meta-stats">
                  <span>Feb 10, 2026</span>
                  <span className="blog-detail-meta-dot">•</span>
                  <span>8 min read</span>
                </div>
              </div>
            </div>

            <div className="blog-detail-social-share">
              <button
                type="button"
                className="social-share-btn"
                aria-label="Share on Twitter"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                </svg>
              </button>
              <button
                type="button"
                className="social-share-btn"
                aria-label="Share on LinkedIn"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </button>
              <button
                type="button"
                className="social-share-btn"
                aria-label="Copy link"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="blog-detail-layout">
        <article className="blog-detail-content">
          <section id="introduction" className="blog-detail-section">
            <h2>Introduction</h2>
            <p>
              React has become the most popular JavaScript library for building
              user interfaces, and mastering it is essential for any frontend
              developer in 2026. This comprehensive guide will walk you through
              everything you need to know to become a proficient React
              developer.
            </p>
            <p>
              Whether you're just starting out or looking to level up your
              skills, this roadmap covers all the fundamental concepts, best
              practices, and advanced techniques you'll need to build
              production-ready applications.
            </p>
          </section>

          <section id="core-concepts" className="blog-detail-section">
            <h2>Core Concepts</h2>
            <p>
              Understanding React's core concepts is crucial before diving into
              advanced topics. Let's explore the foundational building blocks
              that every React developer must master.
            </p>

            <h3>Components and JSX</h3>
            <p>
              Components are the heart of React. They let you split the UI into
              independent, reusable pieces. JSX is a syntax extension that looks
              similar to HTML but allows you to write markup inside JavaScript.
            </p>

            <ul>
              <li>Functional components and hooks</li>
              <li>Props and state management</li>
              <li>Component composition patterns</li>
              <li>Lifecycle methods and effects</li>
              <li>Context API for global state</li>
            </ul>

            <div className="blog-detail-callout">
              <strong>Pro Tip:</strong> Always prefer functional components with
              hooks over class components. They're more concise, easier to test,
              and align with modern React best practices.
            </div>
          </section>

          <section id="example-code" className="blog-detail-section">
            <h2>Example Code</h2>
            <p>
              Let's look at a practical example of a React component using hooks
              for state management and side effects.
            </p>

            <div className="blog-detail-code-block">
              <pre>
                <code>{`import React, { useState, useEffect } from 'react';

function UserProfile({ userId }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUser() {
      try {
        const response = await fetch(\`/api/users/\${userId}\`);
        const data = await response.json();
        setUser(data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchUser();
  }, [userId]);

  if (loading) return <div>Loading...</div>;
  if (!user) return <div>User not found</div>;

  return (
    <div className="user-profile">
      <h2>{user.name}</h2>
      <p>{user.bio}</p>
    </div>
  );
}`}</code>
              </pre>
            </div>

            <p>
              This example demonstrates proper use of useState for local state
              and useEffect for data fetching with proper cleanup and dependency
              management.
            </p>
          </section>

          <section id="best-practices" className="blog-detail-section">
            <h2>Best Practices</h2>
            <p>
              Following best practices ensures your React applications are
              maintainable, performant, and scalable. Here are essential
              guidelines every React developer should follow.
            </p>

            <blockquote className="blog-detail-quote">
              "The best code is no code at all. Every line of code you write is
              a liability." - Jeff Atwood
            </blockquote>

            <h3>Performance Optimization</h3>
            <ul>
              <li>Use React.memo() to prevent unnecessary re-renders</li>
              <li>Implement code splitting with React.lazy() and Suspense</li>
              <li>Optimize context usage to avoid prop drilling</li>
              <li>Use useMemo and useCallback hooks appropriately</li>
              <li>Implement virtualization for long lists</li>
            </ul>

            <h3>Code Organization</h3>
            <p>
              Keep your components small and focused on a single responsibility.
              Extract reusable logic into custom hooks. Use a consistent folder
              structure and naming conventions throughout your project.
            </p>
          </section>

          <section id="conclusion" className="blog-detail-section">
            <h2>Conclusion</h2>
            <p>
              Mastering React is a journey that requires consistent practice and
              staying updated with the latest features and best practices. This
              roadmap provides a solid foundation, but remember that the best
              way to learn is by building real projects.
            </p>
            <p>
              Start with small applications, gradually increase complexity, and
              don't be afraid to experiment with new patterns and libraries.
              Join the React community, contribute to open source, and keep
              learning. Happy coding!
            </p>
          </section>
        </article>

        <aside className="blog-detail-toc">
          <div className="blog-detail-toc-sticky">
            <h3 className="blog-detail-toc-title">Table of Contents</h3>
            <nav className="blog-detail-toc-nav">
              <button
                type="button"
                className={`blog-detail-toc-link ${activeSection === "introduction" ? "active" : ""}`}
                onClick={() => scrollToSection("introduction")}
              >
                Introduction
              </button>
              <button
                type="button"
                className={`blog-detail-toc-link ${activeSection === "core-concepts" ? "active" : ""}`}
                onClick={() => scrollToSection("core-concepts")}
              >
                Core Concepts
              </button>
              <button
                type="button"
                className={`blog-detail-toc-link ${activeSection === "example-code" ? "active" : ""}`}
                onClick={() => scrollToSection("example-code")}
              >
                Example Code
              </button>
              <button
                type="button"
                className={`blog-detail-toc-link ${activeSection === "best-practices" ? "active" : ""}`}
                onClick={() => scrollToSection("best-practices")}
              >
                Best Practices
              </button>
              <button
                type="button"
                className={`blog-detail-toc-link ${activeSection === "conclusion" ? "active" : ""}`}
                onClick={() => scrollToSection("conclusion")}
              >
                Conclusion
              </button>
            </nav>
          </div>
        </aside>
      </div>

      <section className="blog-detail-author-box">
        <div className="blog-detail-author-box-container">
          <img
            src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop"
            alt="Sarah Chen"
            className="blog-detail-author-box-image"
          />
          <div className="blog-detail-author-box-content">
            <h3 className="blog-detail-author-box-name">Sarah Chen</h3>
            <p className="blog-detail-author-box-bio">
              Senior Frontend Engineer with 8+ years of experience building
              scalable web applications. Passionate about React, TypeScript, and
              mentoring developers. Former tech lead at Google.
            </p>
            <button type="button" className="blog-detail-author-box-btn">
              View Profile →
            </button>
          </div>
        </div>
      </section>

      <section className="blog-detail-ai-cta">
        <div className="blog-detail-ai-cta-container">
          <h2 className="blog-detail-ai-cta-title">
            Still Have{" "}
            <span className="blog-detail-ai-cta-prefix">Questions?</span>
          </h2>
          <p className="blog-detail-ai-cta-text">
            Ask our AI Tutor to explain this topic in simpler terms.
          </p>
          <button type="button" className="blog-detail-ai-cta-btn">
            Ask AI About This Article →
          </button>
        </div>
      </section>

      <section className="blog-detail-related">
        <div className="blog-detail-related-container">
          <h2 className="blog-detail-related-title">
            Related <span className="blog-detail-related-prefix">Articles</span>
          </h2>
          <div className="blog-detail-related-grid">
            {relatedArticles.map((article) => (
              <div key={article.id} className="blog-detail-related-card">
                <img
                  src={article.image}
                  alt={article.title}
                  className="blog-detail-related-image"
                />
                <div className="blog-detail-related-content">
                  <h3 className="blog-detail-related-card-title">
                    {article.title}
                  </h3>
                  <span className="blog-detail-related-time">
                    {article.readTime} read
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetailPage;
